import "./style.scss";

import { Collapse } from "@mui/material";
import Plus from "assets/img/svg/plus";
import type { IDistributor } from "components/AdminPanel/types";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import InlineButton from "components/shared/InlineButton";
import ModalBase from "components/shared/ModalBase";
import NameTranslationWrapper from "components/shared/NameTranslationWrapper";
import StyledButton from "components/shared/StyledButton";
import { useCompanySettings } from "hooks/useCompanySettings";
import { t } from "i18next";
import type { SetStateAction } from "react";
import { useEffect, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ChannelCompensationGroup } from "store/channelStore";
import { fetchChannels, fetchCreateChannel, fetchUpdateChannel } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { ChannelCalculationMethod, ChannelKind, ChannelType } from "store/types";

import { channelTypesI18n } from "..";
import NewGroupModal from "../NewGroupModal";
import type { IChannel, TFormChannel, TNewChannelAutocomplete } from "../types";
import { getCalculationMethodConfig } from "./channelCalculationConfig";

/** Form modal component for create / edit channel */
interface ICreateChannel {
    /**
     * component mode
     */
    mode: "new" | "edit";
    /**
     * Channel to edit data
     */
    channel?: IChannel;
    /**
     * used to update current channel data after update channel
     */
    setCurrentChannel?: React.Dispatch<SetStateAction<IChannel | undefined>>;
    /**
     * set current channel id after create to open it details
     */
    setOnTargetChannelId?: React.Dispatch<SetStateAction<number | null>>;
    openModal: boolean;
    closeModal: VoidFunction;
    /** create channel state event */
    setOnCreateChannel?: React.Dispatch<SetStateAction<boolean>>;
}

export const channelCategories = (): TNewChannelAutocomplete[] => {
    return [
        { value: ChannelKind.PHYSICAL, label: t("NewChannel.Physical") },
        { value: ChannelKind.DIGITAL, label: t("NewChannel.Numerical") },
        { value: ChannelKind.RIGHTS_TRANSFER, label: t("NewChannel.RightTransfer") },
        { value: ChannelKind.COLLECTIVE_MANAGEMENT, label: t("NewChannel.AuthorAssociation") },
        { value: ChannelKind.OTHER, label: t("Settings.Other") },
    ];
};

const CreateChannelModal = ({
    mode,
    channel,
    openModal,
    closeModal,
    setCurrentChannel,
    setOnTargetChannelId,
    setOnCreateChannel,
}: ICreateChannel) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const usesInterchannelCompensation = getCompanySettings("uses_interchannel_compensation");
    const usesStocks = getCompanySettings("uses_stock");
    const allowsSubscriptionChannels = getCompanySettings("allows_subscription_channels");
    const distributorList: IDistributor[] = JSON.parse(getCompanySettings("available_distributors"));
    const { channelCompensationGroups, channels } = useAppSelector((state) => state.channels);
    const [loadingBtnStatus, setLoadingBtnStatus] = useState<"idle" | "pending" | "succeed" | "rejected">("idle");
    const [openNewGroupModal, setOpenNewGroupModal] = useState(false);

    const hasCustomId = !!channel?.customIdentifier;
    const [showCustomIdField, setShowCustomIdField] = useState(hasCustomId ?? false);
    // const [openNewDistributorModal, setOpenNewDistributorModal] = useState(false);
    const [newCompensationGroupId, setNewCompensationGroupId] = useState<number | null>(null);

    // Check if a subscription channel already exists
    const hasExistingSubscriptionChannel = channels.some(
        (ch) => ch.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION,
    );

    /** options for autocomplete */
    const calculationMethods: TNewChannelAutocomplete[] = [
        { value: ChannelCalculationMethod.PROPORTIONAL, label: t("NewChannel.Proportional") },
        { value: ChannelCalculationMethod.PACKAGE, label: t("NewChannel.Package") },
        ...(allowsSubscriptionChannels && (mode === "edit" || !hasExistingSubscriptionChannel)
            ? [{ value: ChannelCalculationMethod.SUBSCRIPTION, label: t("NewChannel.Subscription") }]
            : []),
    ];
    const calculType: TNewChannelAutocomplete[] = [
        { value: ChannelType.QUANTITY_BASED, label: t("NewChannel.Quantity") },
        { value: ChannelType.AMOUNT_BASED, label: t("NewChannel.Amount") },
    ];

    /**
     * Used for AlternativeMilestoneAmount.
     */
    const milestoneType: TNewChannelAutocomplete[] = [
        { value: 1, label: channelTypesI18n()[1] }, // quantity
        { value: 0, label: channelTypesI18n()[2] }, // amount
    ];
    const radioBooleanOptions: any = [{ label: t("NewChannel.No") }, { label: t("NewChannel.Yes") }];

    const usesAltAmount = channel?.usesAlternativeMilestoneAmount ? milestoneType[0] : milestoneType[1]; // quantity : amount

    const defaultChannelValues = {
        customIdentifier: channel?.customIdentifier ?? null,
        name: channel?.name ?? "",
        kind: channelCategories().find((category) => category.value == channel?.kind) ?? channelCategories()[0],
        calculationMethod:
            calculationMethods.find((method) => method.value == channel?.calculationMethod) ?? calculationMethods[0],
        type: calculType.find((type) => type.value == channel?.type) ?? calculType[0],
        hasMilestones: channel?.hasMilestones ?? 0,
        distributor:
            distributorList?.find(
                (distributor: { name: string; distributorType: string }) => distributor.name == channel?.distributor,
            ) ?? null,
        compensationGroup: channelCompensationGroups?.find((title) => title.id == channel?.compensationGroupId) ?? null,
        showNotes: channel?.showNotes ?? 1,
        showCumulativeSales: channel?.showCumulativeSales ?? 0,
        showInStock: channel?.showInStock ?? 1,
        usesAlternativeMilestoneAmount: usesAltAmount ?? milestoneType[1],
        nameTranslations: channel?.nameTranslations ?? [],
    };

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isDirty },
    } = useForm<TFormChannel>({
        defaultValues: defaultChannelValues,
        mode: "all",
    });

    const calculationMethod = watch("calculationMethod");
    const channelKind = watch("kind");

    // Get configuration for current calculation method
    const methodConfig = getCalculationMethodConfig(calculationMethod.value);

    // Feature restrictions based on calculation method configuration
    const supportsCompensationGroup = () => methodConfig.supportsCompensationGroup;
    const supportsCumulativeSales = () => methodConfig.supportsCumulativeSales;
    const supportsHasMilestones = () => methodConfig.supportsHasMilestones;

    const allowStock = () => {
        // Stock not allowed for certain methods
        if (!methodConfig.supportsStock) {
            return false;
        }
        /**
         * Stocks follow the copies carried by the sales, whatever the calculation base of the contracts:
         * any physical channel calculated proportionally can track them.
         */
        return (
            channelKind.value === ChannelKind.PHYSICAL &&
            calculationMethod.value === ChannelCalculationMethod.PROPORTIONAL
        );
    };

    channelKind.value === ChannelKind.PHYSICAL && calculationMethod.value === ChannelCalculationMethod.PROPORTIONAL;

    const onSubmit: SubmitHandler<TFormChannel> = async (form) => {
        setLoadingBtnStatus("pending");
        const hasMilestones = +form.hasMilestones;
        const req = {
            customIdentifier: form?.customIdentifier ?? null,
            name: form.name,
            kind: form.kind.value,
            calculationMethod: form.calculationMethod.value,
            hasMilestones,
            distributor: form.distributor?.name ?? form.distributor ?? "",
            compensationGroupId:
                form.compensationGroup?.id && supportsCompensationGroup() ? form.compensationGroup?.id : null,
            showNotes: +form.showNotes,
            showCumulativeSales: +form.showCumulativeSales,
            showInStock: allowStock() ? +form.showInStock : 0,
            nameTranslations: form.nameTranslations ?? [],
        };

        if (mode === "new") {
            const response = await dispatch(fetchCreateChannel(req));
            if (response.meta.requestStatus === "fulfilled") {
                const channelResponse = await dispatch(fetchChannels());
                const newChannel: IChannel = channelResponse.payload.slice(-1)[0];
                closeModal();
                setLoadingBtnStatus("succeed");
                if (setCurrentChannel) setCurrentChannel(newChannel);
                setTimeout(() => {
                    if (setOnTargetChannelId) {
                        setOnTargetChannelId(newChannel.id);
                        setOnCreateChannel && setOnCreateChannel(true);
                    }
                }, 200);
            } else if (response.meta.requestStatus === "rejected") {
                setLoadingBtnStatus("rejected");
            }
        }
        if (mode === "edit") {
            const response = await dispatch(fetchUpdateChannel({ id: channel?.id as number, data: req }));
            if (response.meta.requestStatus === "fulfilled") {
                const channelResponse = await dispatch(fetchChannels());
                const updatedChannel: IChannel = channelResponse.payload.find(
                    (updatedChannel: IChannel) => updatedChannel.id == channel?.id,
                );
                if (setCurrentChannel) setCurrentChannel(updatedChannel);
                closeModal();
                setLoadingBtnStatus("succeed");
            } else if (response.meta.requestStatus === "rejected") {
                setLoadingBtnStatus("rejected");
            }
        }
    };
    useEffect(() => {
        if (calculationMethod.value == ChannelCalculationMethod.PACKAGE) {
            if (mode === "new") {
                // Press forfait / package channels stay amount-based (legacy channel.type semantics).
                setValue("type", calculType[1]);
                setValue("hasMilestones", 0);
                setValue("showCumulativeSales", 0);
            }
        }
        if (calculationMethod.value == ChannelCalculationMethod.SUBSCRIPTION) {
            if (mode === "new") {
                setValue("kind", channelCategories()[1]); // ChannelKind.DIGITAL
                setValue("type", calculType[0]); // ChannelType.QUANTITY_BASED
                setValue("showCumulativeSales", 0);
            }
        }
        if (calculationMethod.value == ChannelCalculationMethod.PROPORTIONAL && mode === "new") {
            // Calculation base is chosen on the contract; new proportional channels default to quantity.
            setValue("type", calculType[0]);
        }
    }, [calculationMethod]);

    // add compensation group
    if (watch("compensationGroup")?.id == -1) {
        setOpenNewGroupModal(true);
        setValue("compensationGroup", null);
    }

    useEffect(() => {
        if (newCompensationGroupId == null) return;
        setValue(
            "compensationGroup",
            channelCompensationGroups?.find((name) => name.id == newCompensationGroupId) ?? null,
        );
    }, [channelCompensationGroups, newCompensationGroupId]);

    // add distributor --> waiting for feature
    // if (watch('distributor')?.id == -1) {
    //     setOpenNewDistributorModal(true);
    //     setValue('distributor', null)
    // }

    const renderCompensationOption = (props: object, option: ChannelCompensationGroup & { addElement?: string }) => {
        return !option.addElement ? (
            <li {...props} className="social-profile-list">
                <span style={{ fontSize: "14px" }}>{option.name}</span>
            </li>
        ) : (
            <li {...props} key={option.name} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        );
    };

    const handleToggleShowCustomId = () => {
        setValue("customIdentifier", null);
        setShowCustomIdField(!showCustomIdField);
    };

    return (
        <>
            {/** New CompensationGroup modal */}
            {openNewGroupModal && (
                <NewGroupModal
                    type="channel"
                    openModal={openNewGroupModal}
                    onClose={() => setOpenNewGroupModal(false)}
                    setNewGroupId={(id: number) => setNewCompensationGroupId(id)}
                />
            )}

            {/** NewDistributor modal */}
            {/** openNewDistributorModal && <NewDistributorModal openModal={openNewDistributorModal} closeModal={() => setOpenNewDistributorModal(false)} />}

            {/** Create/Edit form Modal */}
            <ModalBase
                open={openModal}
                onClose={closeModal}
                modalTitle={mode === "edit" ? t("NewChannel.EditChannel") : t("NewChannel.CreateChannel")}
                contentStyle={{ paddingInline: "0", paddingBlock: "20px 0px" }}
                widthTransition={{ width: "700px", active: true }}
                helpdeskInfo={{
                    text: mode === "new" ? t("Documentation.CreateChannel") : t("Documentation.EditChannel"),
                    link:
                        mode === "new"
                            ? "https://helpdesk.crealo.app/fr/article/creer-un-canal-de-distribution-10p0rcj/"
                            : "https://helpdesk.crealo.app/fr/article/modifier-un-canal-de-distribution-u2m35g/",
                }}
            >
                <div className="channel-form-modal">
                    {/** form */}
                    <form className="channel-form" onSubmit={handleSubmit(onSubmit)}>
                        <div className="channel-name-wrapper">
                            <TextFormComponent
                                label={t("NewChannel.ChannelName")}
                                control={control}
                                controlName="name"
                                placeholder={t("NewChannel.NameHere")}
                                error={errors.name}
                                required
                            />
                            <NameTranslationWrapper control={control} originalName={watch("name")} />
                        </div>
                        <AutocompleteFormComponent
                            label={t("NewChannel.SalesFormat")}
                            control={control}
                            controlName="kind"
                            options={channelCategories()}
                            disabled={calculationMethod.value === ChannelCalculationMethod.SUBSCRIPTION}
                            renderOptionText={(option: TNewChannelAutocomplete) => `${option.label ?? ""}`}
                            isOptionEqualToValue={(option: TNewChannelAutocomplete, value: TNewChannelAutocomplete) =>
                                option.value === value.value
                            }
                            disableClearable
                        />
                        <div className="channel-custom-identifier-wrapper">
                            <InlineButton onClick={handleToggleShowCustomId} open={showCustomIdField}>
                                {t("NewChannel.CustomIdentifier")}
                            </InlineButton>
                            <Collapse in={showCustomIdField} unmountOnExit timeout={"auto"} sx={{ width: "100%" }}>
                                <TextFormComponent
                                    label={t("NewChannel.CustomIdentifier")}
                                    control={control}
                                    controlName="customIdentifier"
                                />
                            </Collapse>
                        </div>
                        <AutocompleteFormComponent
                            label={t("NewChannel.CalculationType")}
                            control={control}
                            controlName="calculationMethod"
                            options={calculationMethods}
                            disabled={mode === "edit"}
                            renderOptionText={(option: TNewChannelAutocomplete) => `${option.label ?? ""}`}
                            isOptionEqualToValue={(option: TNewChannelAutocomplete, value: TNewChannelAutocomplete) =>
                                option.value === value.value
                            }
                            disableClearable
                        />
                        {supportsHasMilestones() && (
                            <RadioFormComponent
                                id="check-hasMilestones"
                                label={t("Channels.HasMilestones")}
                                options={radioBooleanOptions}
                                control={control}
                                controlName="hasMilestones"
                                labelAligned
                            />
                        )}
                        {usesInterchannelCompensation && supportsCompensationGroup() && (
                            <AutocompleteFormComponent
                                label={t("NewChannel.CompensationGroup")}
                                options={channelCompensationGroups ?? []}
                                renderOptionText={(option: ChannelCompensationGroup) =>
                                    option?.name ?? t("NewChannel.NoCompensationGroup")
                                }
                                control={control}
                                controlName="compensationGroup"
                                renderOption={renderCompensationOption}
                                addElement={t("NewChannel.AddCompensationGroup")}
                                defaultFilterOptions
                                isOptionEqualToValue={(
                                    option: ChannelCompensationGroup,
                                    value: ChannelCompensationGroup,
                                ) => option.id === value.id}
                            />
                        )}
                        {watch("kind").value == ChannelKind.DIGITAL && (
                            <AutocompleteFormComponent
                                label={t("NewChannel.Distributor")}
                                control={control}
                                controlName="distributor"
                                options={distributorList ?? []}
                                defaultFilterOptions
                                isOptionEqualToValue={(option: IDistributor, value: IDistributor) =>
                                    option.name == value.name
                                }
                                renderOptionText={(option: IDistributor) => option.name ?? option}
                            />
                        )}
                        <RadioFormComponent
                            id="check-showNotes"
                            label={t("NewChannel.AddDesc")}
                            options={radioBooleanOptions}
                            control={control}
                            controlName="showNotes"
                            labelAligned
                        />
                        {supportsCumulativeSales() && (
                            <RadioFormComponent
                                id="check-showCumulativeSales"
                                label={t("NewChannel.ShowCumulativeSales")}
                                control={control}
                                options={radioBooleanOptions}
                                controlName="showCumulativeSales"
                                labelAligned
                            />
                        )}
                        {usesStocks && allowStock() && (
                            <RadioFormComponent
                                id="check-showInStock"
                                label={t("NewChannel.ShowInStock")}
                                control={control}
                                options={radioBooleanOptions}
                                controlName="showInStock"
                                labelAligned
                            />
                        )}
                        <StyledButton
                            fullWidth
                            type="submit"
                            buttonType="primary"
                            loading={loadingBtnStatus === "pending"}
                            stickyShadow
                        >
                            {mode === "new" ? t("NewChannel.CreateChannel") : t("NewChannel.UpdateChannel")}
                        </StyledButton>
                    </form>
                </div>
            </ModalBase>
        </>
    );
};

export default CreateChannelModal;
